import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../../components/Navbar';
import SidebarSeccion from '../../components/SidebarSeccion';
import { useAuth, API_URL } from '../../context/AuthContext';
import { getTema } from '../../theme';
import { ArrowLeftIcon, CheckIcon, XMarkIcon, ClockIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';

const getYouTubeId = (url) => {
  try {
    const u = new URL(url);
    return u.searchParams.get('v') || u.pathname.split('/').pop();
  } catch { return ''; }
};

export default function EvaluacionVer({ tipo, nivel, evalId, modo }) {
  const { usuario } = useAuth();
  const navigate = useNavigate();
  const tema = getTema(tipo);
  const esProfesor = ['profesor', 'admin'].includes(usuario?.rol);

  const [evaluacion, setEvaluacion] = useState(null);
  const [respuestas, setRespuestas] = useState({});
  const [resultado, setResultado] = useState(null);
  const [resultados, setResultados] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState('');
  const [tiempoRestante, setTiempoRestante] = useState(null);
  const handleEnviarRef = useRef(null);

  // Dudas
  const [dudas, setDudas] = useState([]);
  const [dudasCargando, setDudasCargando] = useState(false);
  const [nuevaDuda, setNuevaDuda] = useState('');
  const [enviandoDuda, setEnviandoDuda] = useState(false);
  const [respondiendoId, setRespondiendoId] = useState(null);
  const [textoRespuesta, setTextoRespuesta] = useState('');
  const [guardandoRespuesta, setGuardandoRespuesta] = useState(false);
  const [mostrarDudas, setMostrarDudas] = useState(false);

  // Redacción
  const [textoRedaccion, setTextoRedaccion] = useState('');
  const [entregaRedaccion, setEntregaRedaccion] = useState(null); // mi entrega (alumno)
  const [entregasRedaccion, setEntregasRedaccion] = useState([]); // todas las entregas (profesor)
  const [enviandoRedaccion, setEnviandoRedaccion] = useState(false);
  const [calificandoId, setCalificandoId] = useState(null);
  const [notaCalif, setNotaCalif] = useState('');
  const [comentarioCalif, setComentarioCalif] = useState('');

  useEffect(() => {
    const cargar = async () => {
      try {
        setCargando(true);
        const res = await axios.get(`${API_URL}/evaluaciones/${evalId}`);
        setEvaluacion(res.data);

        if (modo === 'resultados' && esProfesor) {
          if (res.data.subtipo_eval === 'redaccion') {
            const r = await axios.get(`${API_URL}/evaluaciones/${evalId}/redaccion/entregas`);
            setEntregasRedaccion(r.data);
          } else {
            const r = await axios.get(`${API_URL}/evaluaciones/${evalId}/resultados`);
            setResultados(r.data);
          }
        }

        if (!esProfesor && res.data.subtipo_eval === 'redaccion') {
          try {
            const r = await axios.get(`${API_URL}/evaluaciones/${evalId}/redaccion/mi-entrega`);
            setEntregaRedaccion(r.data);
          } catch { /* sin entrega */ }
        }
      } catch (err) {
        const msg = err.response?.data?.error || '';
        if (msg.includes('nivel') || msg.includes('acceso')) {
          setError(msg || 'No tienes acceso a esta evaluación. Habla con tu profesor/a.');
        } else {
          navigate(-1);
        }
      }
      finally { setCargando(false); }
    };
    cargar();
  }, [evalId, modo]);

  useEffect(() => {
    handleEnviarRef.current = handleEnviar;
  });

  useEffect(() => {
    if (!evalId) return;
    const fetchDudas = async () => {
      setDudasCargando(true);
      try {
        const res = await axios.get(`${API_URL}/dudas/evaluacion/${evalId}`);
        setDudas(res.data);
      } catch { setDudas([]); }
      finally { setDudasCargando(false); }
    };
    fetchDudas();
  }, [evalId]);

  const cargarDudas = async () => {
    try {
      const res = await axios.get(`${API_URL}/dudas/evaluacion/${evalId}`);
      setDudas(res.data);
    } catch { setDudas([]); }
  };

  const enviarDuda = async () => {
    if (!nuevaDuda.trim() || !evaluacion) return;
    setEnviandoDuda(true);
    try {
      await axios.post(`${API_URL}/dudas`, {
        evaluacion_id: parseInt(evalId),
        tipo, nivel,
        evaluacion_titulo: evaluacion.titulo,
        pregunta: nuevaDuda.trim(),
      });
      setNuevaDuda('');
      cargarDudas();
    } catch {} finally { setEnviandoDuda(false); }
  };

  const responderDuda = async (dudaId) => {
    if (!textoRespuesta.trim()) return;
    setGuardandoRespuesta(true);
    try {
      await axios.put(`${API_URL}/dudas/${dudaId}/responder`, { respuesta: textoRespuesta.trim() });
      setRespondiendoId(null);
      setTextoRespuesta('');
      cargarDudas();
    } catch {} finally { setGuardandoRespuesta(false); }
  };

  useEffect(() => {
    if (!evaluacion?.duracion_minutos || resultado || evaluacion.intento || modo !== 'test' || evaluacion.subtipo_eval === 'redaccion') return;
    const total = evaluacion.duracion_minutos * 60;
    setTiempoRestante(total);
    const interval = setInterval(() => {
      setTiempoRestante(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          handleEnviarRef.current(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [evaluacion]);

  const handleEnviar = async (autoEnvio = false) => {
    if (!autoEnvio) {
      for (const p of evaluacion.preguntas) {
        if (p.tipo_pregunta === 'rellenar_huecos') {
          const numHuecos = (p.texto.match(/___/g) || []).length;
          const vals = Array.isArray(respuestas[p.id]) ? respuestas[p.id] : [];
          if (vals.length < numHuecos || vals.some(v => !v?.trim()))
            return alert('Rellena todos los huecos antes de enviar');
        } else {
          if (!respuestas[p.id]) return alert('Responde todas las preguntas antes de enviar');
        }
      }
    }
    setEnviando(true);
    try {
      const res = await axios.post(`${API_URL}/evaluaciones/${evalId}/intentar`, { respuestas });
      setResultado(res.data);
    } catch (e) {
      alert(e.response?.data?.error || 'Error al enviar');
    } finally { setEnviando(false); }
  };

  const handleEnviarRedaccion = async () => {
    if (!textoRedaccion.trim()) return alert('Escribe tu redacción antes de enviar');
    setEnviandoRedaccion(true);
    try {
      await axios.post(`${API_URL}/evaluaciones/${evalId}/redaccion`, { texto_respuesta: textoRedaccion });
      setEntregaRedaccion({ estado: 'pendiente', texto_respuesta: textoRedaccion });
    } catch (e) {
      alert(e.response?.data?.error || 'Error al enviar');
    } finally { setEnviandoRedaccion(false); }
  };

  const handleCalificar = async (rid) => {
    if (notaCalif === '' || isNaN(parseFloat(notaCalif))) return alert('Introduce una nota válida');
    try {
      await axios.put(`${API_URL}/evaluaciones/redacciones/${rid}/calificar`, {
        nota: parseFloat(notaCalif), comentario: comentarioCalif || null,
      });
      setEntregasRedaccion(prev => prev.map(e =>
        e.id === rid ? { ...e, nota: parseFloat(notaCalif), comentario: comentarioCalif || null, estado: 'corregida' } : e
      ));
      setCalificandoId(null);
      setNotaCalif('');
      setComentarioCalif('');
    } catch (e) {
      alert(e.response?.data?.error || 'Error al calificar');
    }
  };

  const abrirSeccionDudas = () => {
    const nuevoEstado = !mostrarDudas;
    setMostrarDudas(nuevoEstado);
    if (nuevoEstado && !esProfesor) {
      axios.put(`${API_URL}/dudas/evaluacion/${evalId}/marcar-leidas`).catch(() => {});
      setDudas(prev => prev.map(d => ({ ...d, leida_alumno: d.respondida ? 1 : d.leida_alumno })));
    }
  };

  const renderDudasSection = () => {
    const sinLeer = !esProfesor ? dudas.filter(d => d.respondida && !d.leida_alumno).length : 0;
    return (
      <div style={{ marginTop: '28px', background: tema.bgCard, border: `1px solid ${sinLeer > 0 ? '#e74c3c55' : tema.border}`, borderRadius: '16px', overflow: 'hidden' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', padding: '14px 20px', background: sinLeer > 0 ? 'rgba(231,76,60,0.05)' : `${tema.accent}06` }}
          onClick={abrirSeccionDudas}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <ChatBubbleLeftRightIcon style={{ width: 17, height: 17, color: sinLeer > 0 ? '#e74c3c' : tema.accent }} />
            <span style={{ color: sinLeer > 0 ? '#e74c3c' : tema.text, fontWeight: 600, fontSize: '14px' }}>Dudas</span>
            {sinLeer > 0 && <span style={{ background: '#e74c3c', color: '#fff', fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '10px' }}>{sinLeer} nueva{sinLeer > 1 ? 's' : ''}</span>}
            {sinLeer === 0 && dudas.length > 0 && <span style={{ background: `${tema.accent}18`, color: tema.accent, fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '10px' }}>{dudas.length}</span>}
          </div>
          <span style={{ color: tema.muted, fontSize: '12px' }}>{mostrarDudas ? '▲ Cerrar' : '▼ Abrir'}</span>
        </div>

        {mostrarDudas && (
          <div style={{ borderTop: `1px solid ${tema.border}` }}>
            <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '18px', maxHeight: '380px', overflowY: 'auto' }}>
              {dudasCargando ? (
                <p style={{ color: tema.muted, textAlign: 'center', padding: '16px' }}>Cargando...</p>
              ) : dudas.length === 0 ? (
                <p style={{ color: tema.muted, textAlign: 'center', padding: '16px', fontSize: '13px' }}>
                  {esProfesor ? 'No hay dudas aún' : 'No has enviado dudas todavía'}
                </p>
              ) : dudas.map(d => (
                <div key={d.id}>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: `${tema.accent}22`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: tema.accent, fontSize: '12px', fontWeight: 700, flexShrink: 0 }}>
                      {d.alumno_nombre?.charAt(0).toUpperCase()}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        {esProfesor && <span style={{ color: tema.text, fontWeight: 600, fontSize: '12px' }}>{d.alumno_nombre}</span>}
                        <span style={{ color: tema.muted, fontSize: '11px' }}>{new Date(d.fecha_pregunta).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}</span>
                        {!d.respondida && <span style={{ background: 'rgba(234,179,8,0.12)', color: '#d97706', fontSize: '10px', padding: '1px 7px', borderRadius: '8px', fontWeight: 600 }}>⏳ Sin responder</span>}
                      </div>
                      <div style={{ background: tema.bg, border: `1px solid ${tema.border}`, borderRadius: '4px 12px 12px 12px', padding: '9px 13px' }}>
                        <p style={{ color: tema.text, fontSize: '13px', margin: 0, lineHeight: '1.6' }}>{d.pregunta}</p>
                      </div>
                    </div>
                  </div>

                  {d.respondida && d.respuesta && respondiendoId !== d.id && (
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', paddingLeft: '42px', marginTop: '8px' }}>
                      <div style={{ width: 32, height: 32, borderRadius: '50%', background: `linear-gradient(135deg, ${tema.primary}, ${tema.accent})`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '12px', fontWeight: 700, flexShrink: 0 }}>P</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                          <span style={{ color: tema.text, fontWeight: 600, fontSize: '12px' }}>Profesor</span>
                          {d.fecha_respuesta && <span style={{ color: tema.muted, fontSize: '11px' }}>{new Date(d.fecha_respuesta).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}</span>}
                          {esProfesor && <button onClick={() => { setRespondiendoId(d.id); setTextoRespuesta(d.respuesta); }} style={{ background: 'none', border: 'none', color: tema.muted, fontSize: '11px', cursor: 'pointer', textDecoration: 'underline' }}>Editar</button>}
                        </div>
                        <div style={{ background: `${tema.accent}12`, border: `1px solid ${tema.accent}28`, borderRadius: '12px 4px 12px 12px', padding: '9px 13px' }}>
                          <p style={{ color: tema.text, fontSize: '13px', margin: 0, lineHeight: '1.6' }}>{d.respuesta}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {esProfesor && (!d.respondida || respondiendoId === d.id) && (
                    <div style={{ paddingLeft: '42px', marginTop: '8px' }}>
                      {respondiendoId === d.id ? (
                        <div>
                          <textarea value={textoRespuesta} onChange={e => setTextoRespuesta(e.target.value)} placeholder="Escribe tu respuesta..."
                            style={{ width: '100%', padding: '9px 13px', borderRadius: '10px', background: tema.bg, border: `1px solid ${tema.accent}`, color: tema.text, fontSize: '13px', resize: 'vertical', minHeight: '70px', boxSizing: 'border-box' }} autoFocus />
                          <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                            <button onClick={() => responderDuda(d.id)} disabled={guardandoRespuesta || !textoRespuesta.trim()}
                              style={{ background: tema.primary, border: 'none', color: '#fff', padding: '7px 18px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
                              {guardandoRespuesta ? '...' : '↩ Enviar'}
                            </button>
                            <button onClick={() => { setRespondiendoId(null); setTextoRespuesta(''); }}
                              style={{ background: 'none', border: `1px solid ${tema.border}`, color: tema.muted, padding: '7px 12px', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' }}>Cancelar</button>
                          </div>
                        </div>
                      ) : (
                        <button onClick={() => { setRespondiendoId(d.id); setTextoRespuesta(''); }}
                          style={{ background: `${tema.accent}12`, border: `1px solid ${tema.accent}30`, color: tema.accent, padding: '6px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
                          ↩ Responder
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {!esProfesor && (
              <div style={{ borderTop: `1px solid ${tema.border}`, padding: '12px 20px', display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
                <textarea value={nuevaDuda} onChange={e => setNuevaDuda(e.target.value)} placeholder="Escribe tu duda..." rows={2}
                  style={{ flex: 1, padding: '9px 13px', borderRadius: '10px', background: tema.bg, border: `1px solid ${tema.border}`, color: tema.text, fontSize: '13px', resize: 'none', boxSizing: 'border-box', lineHeight: '1.5', outline: 'none' }}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviarDuda(); } }}
                />
                <button onClick={enviarDuda} disabled={enviandoDuda || !nuevaDuda.trim()}
                  style={{ background: nuevaDuda.trim() ? tema.primary : tema.border, border: 'none', color: '#fff', padding: '9px 16px', borderRadius: '10px', fontSize: '13px', fontWeight: 600, cursor: nuevaDuda.trim() ? 'pointer' : 'default', transition: 'background 0.2s', whiteSpace: 'nowrap' }}>
                  {enviandoDuda ? '...' : 'Enviar'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (cargando) return <div style={{ minHeight: '100vh', background: tema.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: tema.muted }}>Cargando...</div>;

  if (error) return (
    <div style={{ minHeight: '100vh', background: tema.bg }}>
      <Navbar tema={tema} titulo={tipo === 'militar' ? 'Programa Militar' : 'Inglés Civil'} tipo={tipo} nivel={nivel} />
      <div className="page-layout">
        <SidebarSeccion tema={tema} tipo={tipo} nivel={nivel} />
        <main className="page-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: tema.bgCard, border: '1px solid rgba(231,76,60,0.3)', borderRadius: '16px', padding: '48px', textAlign: 'center', maxWidth: '480px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
            <h2 style={{ color: tema.text, marginBottom: '12px' }}>Sin acceso</h2>
            <p style={{ color: tema.muted, marginBottom: '24px' }}>{error}</p>
            <button onClick={() => navigate(`/${tipo}/${nivel}/actividades`)}
              style={{ background: tema.primary, border: 'none', color: '#fff', padding: '10px 24px', borderRadius: '50px', fontSize: '14px', cursor: 'pointer', boxShadow: '0 4px 14px rgba(139,111,212,0.4)' }}>
              Volver a actividades
            </button>
          </div>
        </main>
      </div>
    </div>
  );

  if (!evaluacion) return null;

  const inputStyle = { width: '100%', padding: '10px 14px', borderRadius: '8px', background: tema.bg, border: `1px solid ${tema.border}`, color: tema.text, fontSize: '14px', boxSizing: 'border-box' };
  const labelStyle = { color: tema.muted, fontSize: '12px', display: 'block', marginBottom: '6px' };

  // ── Vista resultados (profesor) ──────────────────────────────────────────────
  if (modo === 'resultados') {
    const esRedaccion = evaluacion.subtipo_eval === 'redaccion';
    return (
      <div style={{ minHeight: '100vh', background: tema.bg }}>
        <Navbar tema={tema} titulo={tipo === 'militar' ? 'Programa Militar' : 'Inglés Civil'} tipo={tipo} nivel={nivel} />
        <div className="page-layout">
          <SidebarSeccion tema={tema} tipo={tipo} nivel={nivel} />
          <main className="page-content">
            <div className="fade-in">
              <button onClick={() => navigate(-1)} style={{ background: `${tema.accent}15`, border: `1px solid ${tema.accent}44`, color: tema.accent, cursor: 'pointer', marginBottom: '24px', fontSize: '13px', fontWeight: 600, padding: '8px 18px', borderRadius: '50px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <ArrowLeftIcon style={{ width: 15, height: 15 }} /> Volver
              </button>
              <h2 style={{ color: tema.text, fontSize: '24px', marginBottom: '6px' }}>
                {esRedaccion ? '✍️' : '📊'} {evaluacion.titulo}
              </h2>
              <p style={{ color: tema.muted, fontSize: '14px', marginBottom: '28px' }}>
                {esRedaccion ? `${entregasRedaccion.length} entregas recibidas` : `${resultados.length} alumno(s) han realizado esta evaluación`}
              </p>

              {renderDudasSection()}

              {esRedaccion ? (
                entregasRedaccion.length === 0 ? (
                  <div style={{ color: tema.muted, textAlign: 'center', padding: '48px', background: tema.bgCard, borderRadius: '12px', marginTop: '24px' }}>
                    Ningún alumno ha enviado su redacción aún
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '24px' }}>
                    {entregasRedaccion.map(e => (
                      <div key={e.id} style={{ background: tema.bgCard, border: `1px solid ${e.estado === 'corregida' ? tema.success + '44' : tema.border}`, borderRadius: '12px', padding: '20px 24px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '10px', marginBottom: '12px' }}>
                          <div>
                            <p style={{ color: tema.text, fontWeight: 600, fontFamily: 'Inter, sans-serif', margin: 0 }}>{e.nombre}</p>
                            <p style={{ color: tema.muted, fontSize: '12px' }}>{e.email} · {new Date(e.fecha_entrega).toLocaleDateString('es-ES')}</p>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            {e.estado === 'corregida' ? (
                              <span style={{ background: `${tema.success}22`, color: tema.success, fontSize: '13px', fontWeight: 600, padding: '4px 12px', borderRadius: '20px' }}>
                                ✓ {e.nota}/10
                              </span>
                            ) : (
                              <span style={{ background: 'rgba(234,179,8,0.15)', color: '#d97706', fontSize: '13px', fontWeight: 600, padding: '4px 12px', borderRadius: '20px' }}>
                                Pendiente
                              </span>
                            )}
                            {e.estado !== 'corregida' && calificandoId !== e.id && (
                              <button onClick={() => { setCalificandoId(e.id); setNotaCalif(''); setComentarioCalif(''); }}
                                style={{ background: tema.primary, border: 'none', color: '#fff', padding: '7px 14px', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' }}>
                                Calificar
                              </button>
                            )}
                          </div>
                        </div>

                        <div style={{ background: tema.bg, border: `1px solid ${tema.border}`, borderRadius: '8px', padding: '14px', marginBottom: calificandoId === e.id ? '14px' : '0' }}>
                          <p style={{ color: tema.muted, fontSize: '11px', marginBottom: '6px' }}>REDACCIÓN DEL ALUMNO</p>
                          <p style={{ color: tema.text, fontSize: '14px', lineHeight: '1.6', whiteSpace: 'pre-wrap', margin: 0 }}>{e.texto_respuesta}</p>
                        </div>

                        {e.comentario && e.estado === 'corregida' && (
                          <p style={{ color: tema.muted, fontSize: '13px', marginTop: '8px' }}>
                            <strong style={{ color: tema.text }}>Comentario:</strong> {e.comentario}
                          </p>
                        )}

                        {calificandoId === e.id && (
                          <div style={{ background: `${tema.accent}08`, border: `1px solid ${tema.accent}33`, borderRadius: '10px', padding: '16px' }}>
                            <p style={{ color: tema.accent, fontWeight: 600, fontSize: '13px', marginBottom: '12px' }}>Calificar redacción</p>
                            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                              <div>
                                <label style={labelStyle}>Nota (0-10) *</label>
                                <input type="number" min="0" max="10" step="0.5" value={notaCalif} onChange={ev => setNotaCalif(ev.target.value)}
                                  style={{ ...inputStyle, width: '80px', padding: '8px 10px', textAlign: 'center' }} />
                              </div>
                              <div style={{ flex: 1, minWidth: '160px' }}>
                                <label style={labelStyle}>Comentario (opcional)</label>
                                <input type="text" value={comentarioCalif} onChange={ev => setComentarioCalif(ev.target.value)}
                                  placeholder="Feedback para el alumno..."
                                  style={{ ...inputStyle, padding: '8px 12px' }} />
                              </div>
                              <button onClick={() => handleCalificar(e.id)}
                                style={{ background: tema.primary, border: 'none', color: '#fff', padding: '9px 18px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
                                Guardar nota
                              </button>
                              <button onClick={() => setCalificandoId(null)}
                                style={{ background: 'none', border: `1px solid ${tema.border}`, color: tema.muted, padding: '9px 14px', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' }}>
                                Cancelar
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )
              ) : (
                resultados.length === 0 ? (
                  <div style={{ color: tema.muted, textAlign: 'center', padding: '48px', background: tema.bgCard, borderRadius: '12px' }}>
                    Ningún alumno ha realizado esta evaluación aún
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {resultados.map(r => (
                      <div key={r.id} style={{ background: tema.bgCard, border: `1px solid ${tema.border}`, borderRadius: '10px', padding: '16px 24px' }}>
                        <div className="card-row">
                          <div>
                            <p style={{ color: tema.text, fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>{r.nombre}</p>
                            <p style={{ color: tema.muted, fontSize: '12px' }}>{r.email} · {new Date(r.fecha).toLocaleDateString('es-ES')}</p>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: '24px', fontWeight: 700, fontFamily: 'Inter, sans-serif', color: r.puntuacion >= 60 ? tema.success : '#e74c3c' }}>
                              {r.puntuacion}%
                            </div>
                            <p style={{ color: tema.muted, fontSize: '12px' }}>{r.total_preguntas} preguntas</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              )}
            </div>
          </main>
        </div>
      </div>
    );
  }

  // ── Resultado tras enviar test ───────────────────────────────────────────────
  if (resultado) {
    return (
      <div style={{ minHeight: '100vh', background: tema.bg }}>
        <Navbar tema={tema} titulo={tipo === 'militar' ? 'Programa Militar' : 'Inglés Civil'} tipo={tipo} nivel={nivel} />
        <div className="page-layout">
          <SidebarSeccion tema={tema} tipo={tipo} nivel={nivel} />
          <main className="page-content">
            <div className="fade-in" style={{ maxWidth: '700px' }}>
              <div style={{ background: tema.bgCard, border: `1px solid ${tema.border}`, borderRadius: '16px', padding: '36px', textAlign: 'center', marginBottom: '28px' }}>
                <div style={{ fontSize: '64px', marginBottom: '12px' }}>
                  {resultado.puntuacion >= 60 ? '🎉' : '📝'}
                </div>
                <h2 style={{ color: tema.text, fontSize: '28px', marginBottom: '8px' }}>
                  {resultado.puntuacion >= 60 ? '¡Bien hecho!' : 'Sigue practicando'}
                </h2>
                <div style={{ fontSize: '56px', fontWeight: 800, fontFamily: 'Inter, sans-serif', color: resultado.puntuacion >= 60 ? tema.success : '#e74c3c', margin: '16px 0' }}>
                  {resultado.puntuacion}%
                </div>
                <p style={{ color: tema.muted }}>
                  {resultado.correctas} de {resultado.total} preguntas correctas
                </p>
              </div>

              <h3 style={{ color: tema.text, fontSize: '18px', marginBottom: '16px' }}>Revisión de respuestas</h3>
              {evaluacion.preguntas.map((p, idx) => {
                const r = resultado.resultado.find(r => r.id === p.id);
                return (
                  <div key={p.id} style={{ background: tema.bgCard, border: `1px solid ${r?.ok ? tema.success + '44' : '#e74c3c44'}`, borderRadius: '10px', padding: '16px 20px', marginBottom: '10px' }}>
                    <div style={{ display: 'flex', gap: '10px', marginBottom: '8px' }}>
                      <span style={{ color: r?.ok ? tema.success : '#e74c3c', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                        {r?.ok ? <CheckIcon style={{ width: 18, height: 18 }} /> : <XMarkIcon style={{ width: 18, height: 18 }} />}
                      </span>
                      <p style={{ color: tema.text, fontFamily: 'Inter, sans-serif' }}>{idx + 1}. {p.texto}</p>
                    </div>
                    <div style={{ marginLeft: '28px', fontSize: '13px' }}>
                      <p style={{ color: tema.muted }}>Tu respuesta: <span style={{ color: r?.ok ? tema.success : '#e74c3c', fontWeight: 600 }}>{r?.seleccionada || 'Sin responder'}</span></p>
                      {!r?.ok && <p style={{ color: tema.success, marginTop: '4px' }}>Correcta: <span style={{ fontWeight: 600 }}>{r?.correcta}</span></p>}
                    </div>
                  </div>
                );
              })}
              <button onClick={() => navigate(`/${tipo}/${nivel}/actividades`)}
                style={{ background: tema.primary, border: 'none', color: '#fff', padding: '12px 28px', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', marginTop: '16px' }}>
                Volver a actividades
              </button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  // ── Redacción ya enviada (alumno) ────────────────────────────────────────────
  if (!esProfesor && evaluacion.subtipo_eval === 'redaccion' && entregaRedaccion) {
    const corregida = entregaRedaccion.estado === 'corregida';
    return (
      <div style={{ minHeight: '100vh', background: tema.bg }}>
        <Navbar tema={tema} titulo={tipo === 'militar' ? 'Programa Militar' : 'Inglés Civil'} tipo={tipo} nivel={nivel} />
        <div className="page-layout">
          <SidebarSeccion tema={tema} tipo={tipo} nivel={nivel} />
          <main className="page-content">
            <div className="fade-in" style={{ maxWidth: '700px' }}>
              <button onClick={() => navigate(-1)} style={{ background: `${tema.accent}15`, border: `1px solid ${tema.accent}44`, color: tema.accent, cursor: 'pointer', marginBottom: '24px', fontSize: '13px', fontWeight: 600, padding: '8px 18px', borderRadius: '50px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <ArrowLeftIcon style={{ width: 15, height: 15 }} /> Volver
              </button>
              <h2 style={{ color: tema.text, fontSize: '24px', marginBottom: '6px' }}>✍️ {evaluacion.titulo}</h2>
              {evaluacion.descripcion && <p style={{ color: tema.muted, fontSize: '14px', marginBottom: '20px' }}>{evaluacion.descripcion}</p>}

              <div style={{ background: tema.bgCard, border: `1px solid ${corregida ? tema.success + '44' : 'rgba(234,179,8,0.3)'}`, borderRadius: '16px', padding: '32px', textAlign: 'center', marginBottom: '24px' }}>
                <div style={{ fontSize: '48px', marginBottom: '12px' }}>{corregida ? '🎉' : '⏳'}</div>
                <h3 style={{ color: tema.text, fontSize: '20px', marginBottom: '8px' }}>
                  {corregida ? '¡Redacción corregida!' : 'Redacción enviada'}
                </h3>
                {corregida ? (
                  <>
                    <div style={{ fontSize: '48px', fontWeight: 800, fontFamily: 'Inter, sans-serif', color: entregaRedaccion.nota >= 5 ? tema.success : '#e74c3c', margin: '12px 0' }}>
                      {entregaRedaccion.nota}/10
                    </div>
                    {entregaRedaccion.comentario && (
                      <p style={{ color: tema.muted, fontSize: '14px', maxWidth: '440px', margin: '0 auto' }}>
                        <strong style={{ color: tema.text }}>Comentario del profesor:</strong><br />{entregaRedaccion.comentario}
                      </p>
                    )}
                  </>
                ) : (
                  <p style={{ color: tema.muted, fontSize: '14px' }}>
                    Tu redacción está pendiente de corrección. El profesor te asignará una nota pronto.
                  </p>
                )}
              </div>

              <div style={{ background: tema.bgCard, border: `1px solid ${tema.border}`, borderRadius: '12px', padding: '20px' }}>
                <p style={{ color: tema.muted, fontSize: '11px', marginBottom: '10px' }}>TU REDACCIÓN</p>
                <p style={{ color: tema.text, fontSize: '14px', lineHeight: '1.7', whiteSpace: 'pre-wrap' }}>{entregaRedaccion.texto_respuesta}</p>
              </div>

              {renderDudasSection()}

              <button onClick={() => navigate(`/${tipo}/${nivel}/actividades`)}
                style={{ background: tema.primary, border: 'none', color: '#fff', padding: '12px 28px', borderRadius: '50px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 14px rgba(139,111,212,0.4)', marginTop: '20px' }}>
                Volver a actividades
              </button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  // ── Test ya realizado ────────────────────────────────────────────────────────
  if (evaluacion.intento) {
    let correccion = [];
    try { correccion = JSON.parse(evaluacion.intento.respuestas); } catch {}
    const pct = evaluacion.intento.puntuacion;

    return (
      <div style={{ minHeight: '100vh', background: tema.bg }}>
        <Navbar tema={tema} titulo={tipo === 'militar' ? 'Programa Militar' : 'Inglés Civil'} tipo={tipo} nivel={nivel} />
        <div style={{ display: 'flex' }}>
          <SidebarSeccion tema={tema} tipo={tipo} nivel={nivel} />
          <main style={{ flex: 1, padding: '32px', overflowY: 'auto' }}>
            <div className="fade-in" style={{ maxWidth: '700px' }}>
              <div style={{ background: tema.bgCard, border: `1px solid ${tema.border}`, borderRadius: '16px', padding: '36px', textAlign: 'center', marginBottom: '28px' }}>
                <div style={{ fontSize: '56px', marginBottom: '10px' }}>{pct >= 60 ? '🎉' : '📝'}</div>
                <h2 style={{ color: tema.text, fontSize: '22px', marginBottom: '6px' }}>
                  {pct >= 60 ? '¡Bien hecho!' : 'Sigue practicando'}
                </h2>
                <div style={{ fontSize: '52px', fontWeight: 800, fontFamily: 'Inter, sans-serif', color: pct >= 60 ? tema.success : '#e74c3c', margin: '10px 0' }}>
                  {pct}%
                </div>
                <p style={{ color: tema.muted, fontSize: '13px' }}>
                  {evaluacion.intento.total_preguntas} preguntas · {new Date(evaluacion.intento.fecha).toLocaleDateString('es-ES')}
                </p>
              </div>

              {correccion.length > 0 && (
                <>
                  <h3 style={{ color: tema.text, fontSize: '18px', marginBottom: '16px' }}>Revisión de respuestas</h3>
                  {evaluacion.preguntas.map((p, idx) => {
                    const r = correccion.find(c => c.id === p.id);
                    return (
                      <div key={p.id} style={{ background: tema.bgCard, border: `1px solid ${r?.ok ? tema.success + '44' : '#e74c3c44'}`, borderRadius: '10px', padding: '16px 20px', marginBottom: '10px' }}>
                        <div style={{ display: 'flex', gap: '10px', marginBottom: '8px' }}>
                          <span style={{ color: r?.ok ? tema.success : '#e74c3c', fontSize: '18px' }}>
                            {r?.ok ? '✓' : '✗'}
                          </span>
                          <p style={{ color: tema.text, fontFamily: 'Inter, sans-serif' }}>{idx + 1}. {p.texto}</p>
                        </div>
                        <div style={{ marginLeft: '28px', fontSize: '13px' }}>
                          <p style={{ color: tema.muted }}>Tu respuesta: <span style={{ color: r?.ok ? tema.success : '#e74c3c', fontWeight: 600 }}>{r?.seleccionada || 'Sin responder'}</span></p>
                          {!r?.ok && <p style={{ color: tema.success, marginTop: '4px' }}>Correcta: <span style={{ fontWeight: 600 }}>{r?.correcta}</span></p>}
                        </div>
                      </div>
                    );
                  })}
                </>
              )}

              {renderDudasSection()}
              <button onClick={() => navigate(`/${tipo}/${nivel}/actividades`)}
                style={{ background: tema.primary, border: 'none', color: '#fff', padding: '12px 28px', borderRadius: '50px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 14px rgba(139,111,212,0.4)', marginTop: '20px' }}>
                Volver a actividades
              </button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  // ── Formulario: Redacción (alumno) ───────────────────────────────────────────
  if (!esProfesor && evaluacion.subtipo_eval === 'redaccion') {
    return (
      <div style={{ minHeight: '100vh', background: tema.bg }}>
        <Navbar tema={tema} titulo={tipo === 'militar' ? 'Programa Militar' : 'Inglés Civil'} tipo={tipo} nivel={nivel} />
        <div className="page-layout">
          <SidebarSeccion tema={tema} tipo={tipo} nivel={nivel} />
          <main className="page-content">
            <div className="fade-in" style={{ maxWidth: '720px' }}>
              <button onClick={() => navigate(-1)} style={{ background: `${tema.accent}15`, border: `1px solid ${tema.accent}44`, color: tema.accent, cursor: 'pointer', marginBottom: '24px', fontSize: '13px', fontWeight: 600, padding: '8px 18px', borderRadius: '50px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <ArrowLeftIcon style={{ width: 15, height: 15 }} /> Volver
              </button>
              <h2 style={{ color: tema.text, fontSize: '26px', marginBottom: '4px' }}>✍️ {evaluacion.titulo}</h2>

              {evaluacion.descripcion && (
                <div style={{ background: `${tema.accent}10`, border: `1px solid ${tema.accent}33`, borderRadius: '10px', padding: '16px 20px', marginBottom: '24px' }}>
                  <p style={{ color: tema.muted, fontSize: '11px', marginBottom: '6px' }}>INSTRUCCIONES</p>
                  <p style={{ color: tema.text, fontSize: '14px', lineHeight: '1.6' }}>{evaluacion.descripcion}</p>
                </div>
              )}

              <div style={{ background: tema.bgCard, border: `1px solid ${tema.border}`, borderRadius: '12px', padding: '20px', marginBottom: '16px' }}>
                <label style={{ ...labelStyle, marginBottom: '10px', fontSize: '13px' }}>Tu redacción</label>
                <textarea
                  value={textoRedaccion}
                  onChange={e => setTextoRedaccion(e.target.value)}
                  placeholder="Escribe tu redacción aquí..."
                  style={{ ...inputStyle, height: '280px', resize: 'vertical', lineHeight: '1.7' }}
                />
                <p style={{ color: tema.muted, fontSize: '12px', marginTop: '8px' }}>
                  {textoRedaccion.length} caracteres · {textoRedaccion.trim().split(/\s+/).filter(Boolean).length} palabras
                </p>
              </div>

              <button onClick={handleEnviarRedaccion} disabled={enviandoRedaccion || !textoRedaccion.trim()}
                style={{ background: tema.primary, border: 'none', color: '#fff', padding: '13px 32px', borderRadius: '50px', fontSize: '15px', fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 14px rgba(139,111,212,0.4)', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                {enviandoRedaccion ? 'Enviando...' : <><CheckIcon style={{ width: 18, height: 18 }} /> Enviar redacción</>}
              </button>

              {renderDudasSection()}
            </div>
          </main>
        </div>
      </div>
    );
  }

  // ── Formulario: Test (Writing/Listening/Reading) ─────────────────────────────
  const esListening = evaluacion.categoria === 'Listening';
  const esReading = evaluacion.categoria === 'Reading';

  return (
    <div style={{ minHeight: '100vh', background: tema.bg }}>
      <Navbar tema={tema} titulo={tipo === 'militar' ? 'Programa Militar' : 'Inglés Civil'} tipo={tipo} nivel={nivel} />
      <div className="page-layout">
        <SidebarSeccion tema={tema} tipo={tipo} nivel={nivel} />
        <main className="page-content">
          <div className="fade-in" style={{ maxWidth: '720px' }}>
            <button onClick={() => navigate(-1)} style={{ background: `${tema.accent}15`, border: `1px solid ${tema.accent}44`, color: tema.accent, cursor: 'pointer', marginBottom: '24px', fontSize: '13px', fontWeight: 600, padding: '8px 18px', borderRadius: '50px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <ArrowLeftIcon style={{ width: 15, height: 15 }} /> Volver
            </button>
            <h2 style={{ color: tema.text, fontSize: '26px', marginBottom: '4px' }}>{evaluacion.titulo}</h2>
            {evaluacion.descripcion && <p style={{ color: tema.muted, fontSize: '14px', marginBottom: '12px' }}>{evaluacion.descripcion}</p>}
            <p style={{ color: tema.accent, fontSize: '13px', marginBottom: '20px', fontFamily: 'Inter, sans-serif' }}>
              {evaluacion.preguntas.length} preguntas · Responde todas las preguntas
            </p>

            {/* YouTube embed (Listening) */}
            {esListening && evaluacion.link_audio && (
              <div style={{ marginBottom: '24px', borderRadius: '12px', overflow: 'hidden', border: `1px solid ${tema.border}` }}>
                <div style={{ background: `${tema.accent}10`, padding: '10px 16px', borderBottom: `1px solid ${tema.border}` }}>
                  <p style={{ color: tema.accent, fontSize: '13px', fontWeight: 600, margin: 0 }}>🎧 Escucha el audio antes de responder</p>
                </div>
                <iframe
                  src={`https://www.youtube.com/embed/${getYouTubeId(evaluacion.link_audio)}`}
                  title="Audio Listening"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={{ width: '100%', height: '280px', display: 'block', border: 'none' }}
                />
              </div>
            )}

            {/* Texto de lectura (Reading) */}
            {esReading && evaluacion.texto_lectura && (
              <div style={{ background: tema.bgCard, border: `1px solid ${tema.border}`, borderRadius: '12px', padding: '20px', marginBottom: '24px' }}>
                <p style={{ color: tema.muted, fontSize: '11px', marginBottom: '10px' }}>📖 TEXTO DE LECTURA — lee con atención antes de responder</p>
                <p style={{ color: tema.text, fontSize: '14px', lineHeight: '1.8', whiteSpace: 'pre-wrap' }}>{evaluacion.texto_lectura}</p>
              </div>
            )}

            {/* Cuenta atrás */}
            {tiempoRestante !== null && (
              <div style={{
                position: 'sticky', top: '70px', zIndex: 10,
                background: tiempoRestante < 60 ? 'rgba(231,76,60,0.12)' : tiempoRestante < 120 ? 'rgba(234,179,8,0.12)' : `${tema.accent}12`,
                border: `2px solid ${tiempoRestante < 60 ? '#e74c3c' : tiempoRestante < 120 ? '#d97706' : tema.accent}`,
                borderRadius: '12px', padding: '12px 20px', marginBottom: '28px',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px',
              }}>
                <ClockIcon style={{ width: 22, height: 22, color: tiempoRestante < 60 ? '#e74c3c' : tiempoRestante < 120 ? '#d97706' : tema.accent }} />
                <span style={{ color: tiempoRestante < 60 ? '#e74c3c' : tiempoRestante < 120 ? '#d97706' : tema.accent, fontWeight: 800, fontSize: '24px', fontFamily: 'Inter, sans-serif', letterSpacing: '2px' }}>
                  {String(Math.floor(tiempoRestante / 60)).padStart(2, '0')}:{String(tiempoRestante % 60).padStart(2, '0')}
                </span>
                <span style={{ color: tema.muted, fontSize: '13px' }}>
                  {tiempoRestante < 60 ? '¡Tiempo casi agotado!' : tiempoRestante < 120 ? 'Menos de 2 minutos' : 'restantes'}
                </span>
              </div>
            )}

            {/* Preguntas */}
            {evaluacion.preguntas.map((p, idx) => (
              <div key={p.id} style={{ background: tema.bgCard, border: `1px solid ${tema.border}`, borderRadius: '12px', padding: '20px', marginBottom: '14px' }}>
                {p.tipo_pregunta === 'rellenar_huecos' ? (
                  <div>
                    <p style={{ color: tema.muted, fontSize: '12px', marginBottom: '8px' }}>{idx + 1}.</p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px', color: tema.text, fontSize: '14px', lineHeight: '2.2', fontFamily: 'Inter, sans-serif' }}>
                      {p.texto.split('___').map((parte, i, arr) => {
                        const vals = Array.isArray(respuestas[p.id]) ? respuestas[p.id] : [];
                        return (
                          <React.Fragment key={i}>
                            <span>{parte}</span>
                            {i < arr.length - 1 && (
                              <input type="text" value={vals[i] || ''}
                                onChange={e => {
                                  const nuevasVals = Array.isArray(respuestas[p.id]) ? [...respuestas[p.id]] : Array(arr.length - 1).fill('');
                                  nuevasVals[i] = e.target.value;
                                  setRespuestas({ ...respuestas, [p.id]: nuevasVals });
                                }}
                                style={{ background: tema.bg, border: `2px solid ${tema.accent}`, borderRadius: '6px', color: tema.text, padding: '3px 10px', fontSize: '14px', minWidth: '80px', width: `${Math.max(80, ((vals[i] || '').length + 4) * 9)}px`, outline: 'none', fontFamily: 'Inter, sans-serif' }}
                              />
                            )}
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <>
                    <p style={{ color: tema.text, fontFamily: 'Inter, sans-serif', marginBottom: '16px', fontWeight: 500 }}>
                      {idx + 1}. {p.texto}
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {p.opciones.map((op, oIdx) => (
                        <label key={oIdx} style={{
                          display: 'flex', alignItems: 'center', gap: '10px',
                          background: respuestas[p.id] === op ? `${tema.accent}15` : tema.bg,
                          border: `1px solid ${respuestas[p.id] === op ? tema.accent : tema.border}`,
                          borderRadius: '8px', padding: '10px 14px', cursor: 'pointer', transition: 'all 0.15s',
                        }}>
                          <input type="radio" name={`p${p.id}`} value={op}
                            checked={respuestas[p.id] === op}
                            onChange={() => setRespuestas({ ...respuestas, [p.id]: op })}
                            style={{ accentColor: tema.accent }} />
                          <span style={{ color: tema.text, fontSize: '14px' }}>{op}</span>
                        </label>
                      ))}
                    </div>
                  </>
                )}
              </div>
            ))}

            <button onClick={handleEnviar} disabled={enviando}
              style={{ background: tema.primary, border: 'none', color: '#fff', padding: '13px 32px', borderRadius: '50px', fontSize: '15px', fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 14px rgba(139,111,212,0.4)', marginTop: '8px', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              {enviando ? 'Enviando...' : <><CheckIcon style={{ width: 18, height: 18 }} /> Enviar respuestas</>}
            </button>

            {renderDudasSection()}
          </div>
        </main>
      </div>
    </div>
  );
}
